import React, { useState, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { User, Lock, Eye, EyeOff } from 'lucide-react';
import illustrationUrl from '../assets/login_illustration.jpg';

const Login = () => {
  const { login, currentLogo, loginLogoSize } = useContext(AppContext);
  const [username, setUsername] = useState(() => {
    return localStorage.getItem('clou_remember_id_v1') || '';
  });
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(() => {
    const savedRemember = localStorage.getItem('clou_remember_me_v1');
    if (savedRemember !== null) {
      return savedRemember === 'true';
    }
    return Boolean(localStorage.getItem('clou_remember_id_v1'));
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleUsernameChange = (e) => {
    const val = e.target.value;
    setUsername(val);
    if (rememberMe) {
      if (val.trim()) {
        localStorage.setItem('clou_remember_id_v1', val.trim());
      } else {
        localStorage.removeItem('clou_remember_id_v1');
      }
    }
  };

  const handleRememberToggle = (e) => {
    const checked = e.target.checked;
    setRememberMe(checked);
    localStorage.setItem('clou_remember_me_v1', checked ? 'true' : 'false');
    if (checked) {
      if (username.trim()) {
        localStorage.setItem('clou_remember_id_v1', username.trim());
      }
    } else {
      localStorage.removeItem('clou_remember_id_v1');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim()) {
      alert('아이디 또는 이메일을 입력하세요.');
      return;
    }
    if (!password.trim()) {
      alert('비밀번호를 입력하세요.');
      return;
    }

    if (rememberMe) {
      localStorage.setItem('clou_remember_id_v1', username.trim());
      localStorage.setItem('clou_remember_me_v1', 'true');
    } else {
      localStorage.removeItem('clou_remember_id_v1');
      localStorage.setItem('clou_remember_me_v1', 'false');
    }

    const success = await login(username.trim(), password.trim());
    if (!success) {
      alert('등록되지 않은 사원 계정이거나 입력하신 정보가 일치하지 않습니다.');
    }
  };

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-slate-50 font-sans p-3 sm:p-4 relative overflow-hidden">
      {/* Background Soft Circles */}
      <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full bg-blue-100/50 blur-3xl -z-10" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] rounded-full bg-indigo-50/50 blur-3xl -z-10" />

      {/* Main Container */}
      <div className="w-full max-w-[960px] flex flex-col md:flex-row items-center justify-center md:justify-between gap-4 md:gap-12 z-10 my-auto">
        
        {/* Left Side: Brand Visuals (Compact on Mobile, Full on PC) */}
        <div className="flex flex-col items-center md:items-start text-center md:text-left space-y-2 md:space-y-6">
          <div className="flex flex-col items-center md:items-start space-y-1.5 md:space-y-3">
            <div className="flex items-center justify-center">
              {/* Outer Frame */}
              <div
                className="flex items-center justify-center rounded-2xl border border-blue-200/40 bg-white shadow-xs"
                style={{
                  width: `${loginLogoSize?.width || 120}px`,
                  height: `${loginLogoSize?.height || 120}px`,
                  padding: `${loginLogoSize?.padding !== undefined ? loginLogoSize.padding : 12}px`
                }}
              >
                {/* Logo Image */}
                <img
                  src={currentLogo}
                  alt="Clouflake Logo"
                  className="w-full h-full object-contain animate-fade-in"
                />
              </div>
            </div>
            
            <h1 className="text-2xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
              클로우플레이크
            </h1>
            <p className="text-slate-500 text-xs md:text-base font-medium hidden sm:block">
              AX를 선도하는 클로우플레이크
            </p>
          </div>

          {/* Isometric 3D Illustration - Hidden on mobile to fit within one screen */}
          <div className="hidden md:flex w-[300px] sm:w-[350px] md:w-[380px] h-[300px] sm:h-[350px] md:h-[380px] items-center justify-center bg-white/40 backdrop-blur-md rounded-3xl border border-white/50 shadow-sm p-4 relative overflow-hidden">
            <img 
              src={illustrationUrl} 
              alt="Clouflake Portal" 
              className="w-full h-full object-contain rounded-2xl"
            />
          </div>
        </div>

        {/* Right Side: Login Card (Single-screen Fit on Mobile) */}
        <div className="w-full max-w-[380px] md:max-w-[420px] bg-white border border-slate-100/80 rounded-3xl p-5 sm:p-8 md:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] text-left space-y-4 md:space-y-6">
          <div className="space-y-1 text-center md:text-left">
            <h2 className="text-lg md:text-2xl font-bold text-slate-900 tracking-tight">로그인</h2>
            <p className="text-[11px] md:text-xs text-slate-400 font-bold">클로우플레이크 계정으로 로그인하세요.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4">
            {/* Username Field */}
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-slate-400">
                <User className="w-4 h-4" />
              </span>
              <input
                type="text"
                placeholder="아이디 (이메일)"
                value={username}
                onChange={handleUsernameChange}
                className="w-full pl-10 pr-4 py-2.5 sm:py-3 bg-slate-50 border border-slate-200 focus:bg-white rounded-xl text-xs sm:text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all font-medium"
                required
                autoFocus
              />
            </div>

            {/* Password Field */}
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-slate-400">
                <Lock className="w-4 h-4" />
              </span>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="비밀번호"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-10 py-2.5 sm:py-3 bg-slate-50 border border-slate-200 focus:bg-white rounded-xl text-xs sm:text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all font-medium"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {/* Remember ID Checkbox */}
            <div className="flex items-center justify-start text-xs pt-0.5">
              <label className="flex items-center gap-2 text-slate-500 font-bold cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={handleRememberToggle}
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer w-4 h-4"
                />
                <span className="text-[11px] sm:text-xs">아이디 저장</span>
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full py-3 sm:py-3.5 px-4 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs sm:text-sm rounded-xl shadow-xs hover:shadow-md transition-all cursor-pointer mt-1"
            >
              로그인
            </button>
          </form>

          {/* Security Status Indicator */}
          <div className="pt-2.5 border-t border-slate-100 flex items-center justify-center text-[11px] text-slate-400 font-medium">
            <span className="text-blue-600 font-semibold flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block animate-pulse"></span>
              256-bit SSL/TLS 암호화 보안 연결됨
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
